"use client";

import { BookText, Calendar, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";

export interface WorkResultData {
  doi: string;
  title: string;
  authors: string;
  container: string | null;
  year: number | null;
  type: string;
}

interface WorkResultCardProps {
  work: WorkResultData;
  onSelect: (work: WorkResultData) => void;
}

/** Humanise a Crossref `type` ("journal-article" → "Journal Article") for display. */
function humanizeType(type: string): string {
  if (!type) {
    return "";
  }
  return type
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function WorkResultCard({ work, onSelect }: WorkResultCardProps) {
  const typeLabel = humanizeType(work.type);

  return (
    <div className="group relative flex gap-4 rounded-xl border border-border/60 bg-card p-4 transition-all duration-200 hover:border-border hover:shadow-soft-md">
      {/* Type Icon */}
      <div className="relative shrink-0">
        <div className="flex h-16 w-16 items-center justify-center rounded-md border border-border/40 bg-muted/50">
          <FileText className="size-6 text-muted-foreground/40" />
        </div>
      </div>

      {/* Work Info */}
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex-1">
          <h4 className="mb-1 line-clamp-2 font-medium text-sm leading-snug transition-colors group-hover:text-primary">
            {work.title}
          </h4>
          {work.authors && (
            <p className="line-clamp-1 text-muted-foreground text-xs">
              {work.authors}
            </p>
          )}
        </div>

        <div className="mt-2 flex items-center gap-3 text-muted-foreground text-xs">
          {work.year && (
            <span className="flex items-center gap-1">
              <Calendar className="size-3" />
              {work.year}
            </span>
          )}
          {work.container && (
            <span className="flex max-w-[180px] items-center gap-1 truncate">
              <BookText className="size-3 shrink-0" />
              <span className="truncate">{work.container}</span>
            </span>
          )}
          {typeLabel && (
            <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground/80">
              {typeLabel}
            </span>
          )}
        </div>
      </div>

      {/* Select Button */}
      <div className="flex shrink-0 items-center">
        <Button
          className="opacity-80 transition-opacity group-hover:opacity-100"
          onClick={() => onSelect(work)}
          size="sm"
          type="button"
          variant="outline"
        >
          Select
        </Button>
      </div>
    </div>
  );
}

// Skeleton loading card
export function WorkResultCardSkeleton() {
  return (
    <div className="flex gap-4 rounded-xl border border-border/60 bg-card p-4">
      {/* Icon skeleton */}
      <div className="h-16 w-16 shrink-0 animate-pulse rounded-md bg-muted/70" />

      {/* Content skeleton */}
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="h-4 w-3/4 animate-pulse rounded bg-muted/70" />
        <div className="h-3 w-1/2 animate-pulse rounded bg-muted/70" />
        <div className="flex-1" />
        <div className="flex gap-3">
          <div className="h-3 w-12 animate-pulse rounded bg-muted/70" />
          <div className="h-3 w-20 animate-pulse rounded bg-muted/70" />
        </div>
      </div>

      {/* Button skeleton */}
      <div className="flex shrink-0 items-center">
        <div className="h-8 w-16 animate-pulse rounded-md bg-muted/70" />
      </div>
    </div>
  );
}
